/**
 * Permission Table Component
 *
 * Visual grid showing resources × actions matrix with color-coded permissions
 */

import React, { useState, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Tooltip,
  IconButton
} from '@mui/material';
import {
  Search,
  CheckCircle,
  Cancel,
  Remove,
  FilterList,
  Info
} from '@mui/icons-material';

export default function PermissionTable({ permissions = {}, onGrantClick }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [actionFilter, setActionFilter] = useState('ALL');
  const [permissionFilter, setPermissionFilter] = useState('ALL');

  // Extract all unique actions
  const allActions = useMemo(() => {
    const actions = new Set();
    Object.values(permissions).forEach(resourceActions => {
      Object.keys(resourceActions).forEach(action => actions.add(action));
    });
    return Array.from(actions).sort();
  }, [permissions]);

  // Filter permissions based on search and filters
  const filteredPermissions = useMemo(() => {
    const filtered = {};

    Object.entries(permissions).forEach(([resource, actions]) => {
      // Check resource name filter
      if (searchTerm && !resource.toLowerCase().includes(searchTerm.toLowerCase())) {
        return;
      }

      // Check action filter
      if (actionFilter !== 'ALL') {
        const filteredActions = {};
        Object.entries(actions).forEach(([action, permission]) => {
          if (action === actionFilter) {
            filteredActions[action] = permission;
          }
        });
        if (Object.keys(filteredActions).length === 0) {
          return;
        }
        actions = filteredActions;
      }

      // Check permission filter
      if (permissionFilter !== 'ALL') {
        const hasPermission = Object.values(actions).some(p => p === permissionFilter);
        if (!hasPermission) {
          return;
        }
      }

      filtered[resource] = actions;
    });

    return filtered;
  }, [permissions, searchTerm, actionFilter, permissionFilter]);

  const getPermissionIcon = (permission) => {
    switch (permission) {
      case 'ALLOW':
        return <CheckCircle sx={{ color: 'success.main', fontSize: 20 }} />;
      case 'DENY':
        return <Cancel sx={{ color: 'error.main', fontSize: 20 }} />;
      default:
        return <Remove sx={{ color: 'text.disabled', fontSize: 20 }} />;
    }
  };

  const getPermissionColor = (permission) => {
    switch (permission) {
      case 'ALLOW':
        return '#e8f5e9';
      case 'DENY':
        return '#ffebee';
      default:
        return 'transparent';
    }
  };

  const getPermissionChipColor = (permission) => {
    switch (permission) {
      case 'ALLOW':
        return 'success';
      case 'DENY':
        return 'error';
      default:
        return 'default';
    }
  };

  // Calculate statistics
  const stats = useMemo(() => {
    let totalAllow = 0;
    let totalDeny = 0;

    Object.values(filteredPermissions).forEach(actions => {
      Object.values(actions).forEach(permission => {
        if (permission === 'ALLOW') totalAllow++;
        else if (permission === 'DENY') totalDeny++;
      });
    });

    return { totalAllow, totalDeny };
  }, [filteredPermissions]);

  const resources = Object.keys(filteredPermissions);

  if (resources.length === 0 && !searchTerm && actionFilter === 'ALL' && permissionFilter === 'ALL') {
    return (
      <Box p={3} textAlign="center">
        <Typography variant="h6" color="text.secondary">
          No permissions configured
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <TextField
            size="small"
            placeholder="Search resources..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              )
            }}
            sx={{ flexGrow: 1, maxWidth: 300 }}
          />

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Action</InputLabel>
            <Select
              value={actionFilter}
              label="Action"
              onChange={(e) => setActionFilter(e.target.value)}
            >
              <MenuItem value="ALL">All Actions</MenuItem>
              {allActions.map(action => (
                <MenuItem key={action} value={action}>{action}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Permission</InputLabel>
            <Select
              value={permissionFilter}
              label="Permission"
              onChange={(e) => setPermissionFilter(e.target.value)}
            >
              <MenuItem value="ALL">All Permissions</MenuItem>
              <MenuItem value="ALLOW">Allow Only</MenuItem>
              <MenuItem value="DENY">Deny Only</MenuItem>
            </Select>
          </FormControl>

          <Box sx={{ flexGrow: 1 }} />

          <Stack direction="row" spacing={1}>
            <Chip
              icon={<CheckCircle />}
              label={`Allow: ${stats.totalAllow}`}
              color="success"
              size="small"
              variant="outlined"
            />
            <Chip
              icon={<Cancel />}
              label={`Deny: ${stats.totalDeny}`}
              color="error"
              size="small"
              variant="outlined"
            />
          </Stack>
        </Stack>
      </Paper>

      {/* Permission Matrix */}
      <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
        {resources.length === 0 ? (
          <Box p={3} textAlign="center">
            <Typography variant="body1" color="text.secondary">
              No resources match the current filters
            </Typography>
          </Box>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th
                  style={{
                    padding: '12px',
                    backgroundColor: '#f5f5f5',
                    borderBottom: '2px solid #e0e0e0',
                    borderRight: '2px solid #e0e0e0',
                    position: 'sticky',
                    left: 0,
                    zIndex: 1,
                    minWidth: '150px'
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="subtitle2">Resource</Typography>
                    <FilterList fontSize="small" />
                  </Stack>
                </th>
                {allActions.map(action => (
                  <th
                    key={action}
                    style={{
                      padding: '12px',
                      backgroundColor: '#f5f5f5',
                      borderBottom: '2px solid #e0e0e0',
                      borderRight: '1px solid #e0e0e0',
                      textAlign: 'center',
                      minWidth: '100px'
                    }}
                  >
                    <Typography variant="subtitle2">
                      {action}
                    </Typography>
                  </th>
                ))}
                <th
                  style={{
                    padding: '12px',
                    backgroundColor: '#f5f5f5',
                    borderBottom: '2px solid #e0e0e0',
                    textAlign: 'center',
                    minWidth: '100px'
                  }}
                >
                  <Typography variant="subtitle2">Summary</Typography>
                </th>
              </tr>
            </thead>
            <tbody>
              {resources.map((resource, index) => {
                const resourceActions = filteredPermissions[resource];
                const allowCount = Object.values(resourceActions).filter(p => p === 'ALLOW').length;
                const denyCount = Object.values(resourceActions).filter(p => p === 'DENY').length;

                return (
                  <tr key={resource}>
                    <td
                      style={{
                        padding: '12px',
                        borderBottom: '1px solid #e0e0e0',
                        borderRight: '2px solid #e0e0e0',
                        backgroundColor: index % 2 === 0 ? '#fafafa' : 'white',
                        fontWeight: 500,
                        position: 'sticky',
                        left: 0,
                        zIndex: 1
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="body2">{resource}</Typography>
                        {resourceActions._grantId && (
                          <Tooltip title="View grant that provided this permission">
                            <IconButton
                              size="small"
                              onClick={() => onGrantClick && onGrantClick(resourceActions._grantId)}
                            >
                              <Info fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </td>
                    {allActions.map(action => {
                      const permission = resourceActions[action];
                      return (
                        <td
                          key={action}
                          style={{
                            padding: '8px',
                            borderBottom: '1px solid #e0e0e0',
                            borderRight: '1px solid #e0e0e0',
                            textAlign: 'center',
                            backgroundColor: getPermissionColor(permission)
                          }}
                        >
                          <Tooltip title={`${resource}.${action}: ${permission}`}>
                            <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                              {getPermissionIcon(permission)}
                            </Box>
                          </Tooltip>
                        </td>
                      );
                    })}
                    <td
                      style={{
                        padding: '8px',
                        borderBottom: '1px solid #e0e0e0',
                        textAlign: 'center',
                        backgroundColor: index % 2 === 0 ? '#fafafa' : 'white'
                      }}
                    >
                      <Stack direction="row" spacing={0.5} justifyContent="center">
                        {allowCount > 0 && (
                          <Chip
                            label={allowCount}
                            size="small"
                            color="success"
                            variant="outlined"
                            sx={{ minWidth: 30, height: 20 }}
                          />
                        )}
                        {denyCount > 0 && (
                          <Chip
                            label={denyCount}
                            size="small"
                            color="error"
                            variant="outlined"
                            sx={{ minWidth: 30, height: 20 }}
                          />
                        )}
                        {allowCount === 0 && denyCount === 0 && (
                          <Chip
                            label="—"
                            size="small"
                            variant="outlined"
                            sx={{ minWidth: 30, height: 20 }}
                          />
                        )}
                      </Stack>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr>
                <td
                  style={{
                    padding: '12px',
                    backgroundColor: '#f5f5f5',
                    borderTop: '2px solid #e0e0e0',
                    borderRight: '2px solid #e0e0e0',
                    fontWeight: 'bold',
                    position: 'sticky',
                    left: 0,
                    zIndex: 1
                  }}
                >
                  <Typography variant="subtitle2">
                    Total ({resources.length})
                  </Typography>
                </td>
                {allActions.map(action => {
                  let actionAllow = 0;
                  let actionDeny = 0;

                  resources.forEach(resource => {
                    const permission = filteredPermissions[resource][action];
                    if (permission === 'ALLOW') actionAllow++;
                    else if (permission === 'DENY') actionDeny++;
                  });

                  return (
                    <td
                      key={action}
                      style={{
                        padding: '8px',
                        backgroundColor: '#f5f5f5',
                        borderTop: '2px solid #e0e0e0',
                        borderRight: '1px solid #e0e0e0',
                        textAlign: 'center'
                      }}
                    >
                      <Stack direction="row" spacing={0.5} justifyContent="center">
                        {actionAllow > 0 && (
                          <Typography variant="caption" color="success.main">
                            {actionAllow}
                          </Typography>
                        )}
                        {actionDeny > 0 && (
                          <Typography variant="caption" color="error.main">
                            {actionDeny}
                          </Typography>
                        )}
                        {actionAllow === 0 && actionDeny === 0 && (
                          <Typography variant="caption" color="text.disabled">
                            —
                          </Typography>
                        )}
                      </Stack>
                    </td>
                  );
                })}
                <td
                  style={{
                    padding: '8px',
                    backgroundColor: '#f5f5f5',
                    borderTop: '2px solid #e0e0e0',
                    textAlign: 'center'
                  }}
                >
                  <Stack direction="row" spacing={0.5} justifyContent="center">
                    <Chip
                      label={stats.totalAllow}
                      size="small"
                      color="success"
                      variant="outlined"
                      sx={{ minWidth: 35, height: 22 }}
                    />
                    <Chip
                      label={stats.totalDeny}
                      size="small"
                      color="error"
                      variant="outlined"
                      sx={{ minWidth: 35, height: 22 }}
                    />
                  </Stack>
                </td>
              </tr>
            </tfoot>
          </table>
        )}
      </Paper>

      {/* Legend */}
      <Stack direction="row" spacing={2} sx={{ mt: 2, justifyContent: 'center' }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <CheckCircle sx={{ color: 'success.main' }} />
          <Typography variant="body2">Allow</Typography>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          <Cancel sx={{ color: 'error.main' }} />
          <Typography variant="body2">Deny</Typography>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          <Remove sx={{ color: 'text.disabled' }} />
          <Typography variant="body2">Not Set (Denied by Default)</Typography>
        </Stack>
      </Stack>
    </Box>
  );
}